import { DashboardShell } from "@/components/dashboard-shell"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Badge } from "@/components/ui/badge"
import { TrendingUp, TrendingDown, DollarSign, Zap, Users, Target } from "lucide-react"
import { Area, AreaChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"

const investmentData = [
  { month: "Jan", investment: 145, deals: 8 },
  { month: "Feb", investment: 167, deals: 12 },
  { month: "Mar", investment: 189, deals: 10 },
  { month: "Apr", investment: 223, deals: 15 },
  { month: "May", investment: 198, deals: 11 },
  { month: "Jun", investment: 256, deals: 18 },
  { month: "Jul", investment: 234, deals: 14 },
  { month: "Aug", investment: 278, deals: 16 },
  { month: "Sep", investment: 312, deals: 20 },
  { month: "Oct", investment: 289, deals: 17 },
  { month: "Nov", investment: 334, deals: 22 },
  { month: "Dec", investment: 367, deals: 25 },
]

const capacityData = [
  { quarter: "Q1 2024", planned: 850, operational: 650 },
  { quarter: "Q2 2024", planned: 920, operational: 720 },
  { quarter: "Q3 2024", planned: 1050, operational: 810 },
  { quarter: "Q4 2024", planned: 1180, operational: 920 },
  { quarter: "Q1 2025", planned: 1320, operational: 1050 },
  { quarter: "Q2 2025", planned: 1450, operational: 1180 },
]

const performanceMetrics = [
  { metric: "Portfolio Value", value: "£2.4B", change: "+12.5%", trend: "up" },
  { metric: "Active Projects", value: "43", change: "+8", trend: "up" },
  { metric: "Total Capacity", value: "2.8GW", change: "+450MW", trend: "up" },
  { metric: "Avg Gate 2 Score", value: "8.2", change: "+0.3", trend: "up" },
  { metric: "Deal Velocity", value: "18/mo", change: "+2", trend: "up" },
  { metric: "Market Share", value: "15.3%", change: "-0.2%", trend: "down" },
]

const sectorPerformance = [
  { sector: "Offshore Wind", projects: 18, capacity: "1.2GW", investment: "£1.2B", growth: "+28%" },
  { sector: "Onshore Wind", projects: 15, capacity: "850MW", investment: "£680M", growth: "+15%" },
  { sector: "Solar", projects: 22, capacity: "650MW", investment: "£520M", growth: "+22%" },
  { sector: "Energy Storage", projects: 8, capacity: "180MW", investment: "£340M", growth: "+45%" },
]

export default function AnalyticsPage() {
  return (
    <DashboardShell>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Analytics</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground">Portfolio performance and market intelligence</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {performanceMetrics.map((item, idx) => (
              <Card key={idx}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{item.metric}</CardTitle>
                  {item.trend === "up" ? (
                    <TrendingUp className="h-4 w-4 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-500" />
                  )}
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{item.value}</div>
                  <p className={`text-xs ${item.trend === "up" ? "text-green-500" : "text-red-500"}`}>
                    {item.change} from last period
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="investment" className="w-full">
            <TabsList>
              <TabsTrigger value="investment">Investment Trends</TabsTrigger>
              <TabsTrigger value="capacity">Capacity Analysis</TabsTrigger>
              <TabsTrigger value="sector">Sector Performance</TabsTrigger>
            </TabsList>

            <TabsContent value="investment" className="mt-6 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Investment Flow Analysis</CardTitle>
                  <CardDescription>Monthly investment volume and deal count trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{
                      investment: {
                        label: "Investment (£M)",
                        color: "hsl(var(--primary))",
                      },
                      deals: {
                        label: "Deals",
                        color: "#10b981",
                      },
                    }}
                    className="h-[400px]"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={investmentData}>
                        <defs>
                          <linearGradient id="colorInvestment" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--color-investment)" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="var(--color-investment)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="investment"
                          stroke="var(--color-investment)"
                          fillOpacity={1}
                          fill="url(#colorInvestment)"
                          name="Investment (£M)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-primary" />
                      Total Invested
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-primary">£2.99B</div>
                    <p className="text-xs text-muted-foreground mt-1">YTD 2025</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      Total Deals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">188</div>
                    <p className="text-xs text-muted-foreground mt-1">Completed transactions</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary" />
                      Avg Deal Size
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">£15.9M</div>
                    <p className="text-xs text-muted-foreground mt-1">Per transaction</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="capacity" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Capacity Pipeline</CardTitle>
                  <CardDescription>Planned vs operational capacity over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer
                    config={{
                      planned: {
                        label: "Planned (MW)",
                        color: "#3b82f6",
                      },
                      operational: {
                        label: "Operational (MW)",
                        color: "hsl(var(--primary))",
                      },
                    }}
                    className="h-[400px]"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={capacityData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="quarter" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        <Line type="monotone" dataKey="planned" stroke="#3b82f6" strokeWidth={2} name="Planned (MW)" />
                        <Line
                          type="monotone"
                          dataKey="operational"
                          stroke="var(--color-operational)"
                          strokeWidth={2}
                          name="Operational (MW)"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sector" className="mt-6">
              <div className="grid gap-4">
                {sectorPerformance.map((sector, idx) => (
                  <Card key={idx}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap className="h-5 w-5 text-primary" />
                          <CardTitle className="text-lg">{sector.sector}</CardTitle>
                        </div>
                        <Badge variant="default" className="text-sm">
                          {sector.growth}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Projects</div>
                          <div className="text-2xl font-bold">{sector.projects}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Capacity</div>
                          <div className="text-2xl font-bold text-primary">{sector.capacity}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground text-xs mb-1">Investment</div>
                          <div className="text-2xl font-bold">{sector.investment}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </DashboardShell>
  )
}
